import { mirrorX } from "./mirror.js";
import type { TextDirection } from "@yjsgames/i18n";

/**
 * 인라인 아이콘 텍스트 — `{icon:name}` 토큰을 텍스트 흐름 중간에 박는 리치 라벨의 **순수 코어**.
 * (Unity TextMeshPro `<sprite=N>`의 Phaser 대응 — 외부 리치텍스트 라이브러리 없이 경량 구현.)
 *
 * 책임 3단계(전부 Phaser 의존 0 — 측정·텍스처는 호출자/뷰가 주입):
 *   1. `parseInlineTokens` : raw 문자열 → 세그먼트(text / icon / newline)
 *   2. `buildInlineWords`  : 세그먼트 → 공백 경계 단어(폭은 주입된 measure로 측정)
 *   3. `layoutInlineWords` : 단어 → wordWrap-aware 배치(줄바꿈·줄별 중앙정렬·RTL 미러)
 *
 * 아이콘은 **단어 1개**로 취급되어 줄바꿈에서 쪼개지지 않는다. 줄별 중앙정렬은 Canvas `align:"center"`와
 * 동치((maxWidth - lineWidth)/2 독립 오프셋), RTL은 `mirror.ts`의 `mirrorX`를 줄 내부에 재사용한다.
 */

/** 토큰 파싱 1차 산출 — 텍스트 조각 / 아이콘 / 강제 줄바꿈. */
export type InlineSegment =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "icon"; readonly name: string }
  | { readonly kind: "newline" };

/** 폭이 측정된 배치 단위. icon은 trailingSpaceWidth=0(인접 토큰과 붙음). newline은 줄 강제 분리 마커. */
export type InlineWord =
  | { kind: "text"; text: string; width: number; trailingSpaceWidth: number }
  | { kind: "icon"; name: string; width: number; trailingSpaceWidth: number }
  | { kind: "newline" };

/** 배치 결과 1개 — newline은 제외되고 text/icon만 좌표를 가진다. */
export interface InlinePlacement {
  readonly word: Exclude<InlineWord, { kind: "newline" }>;
  readonly x: number;
  readonly y: number;
  readonly line: number;
}

/** `layoutInlineWords`의 배치 산출 결과. 뷰 레이어가 이 값을 기준으로 Phaser 오브젝트를 배치한다. */
export interface InlineLayoutResult {
  readonly placements: InlinePlacement[];
  /** 각 줄의 실제 콘텐츠 폭(trailing space 제외). */
  readonly lineWidths: number[];
  readonly lineCount: number;
  /** 전체 높이 = lineCount × lineHeight. */
  readonly totalHeight: number;
  /** 가장 넓은 줄 폭 — 컨테이너 displayWidth 용. */
  readonly maxLineWidth: number;
}

export interface InlineLayoutOptions {
  readonly maxWidth: number;
  readonly lineHeight: number;
  readonly align: "center" | "start";
  readonly dir: TextDirection;
}

// `{icon:start}` — name은 \w+ (영숫자+언더스코어). 콜론 없는 `{foo}`나 빈 `{icon:}`은 매칭 안 되어 텍스트로 통과.
// i18n Translator의 placeholder 정규식 `/\{(\w+)\}/g`와도 충돌하지 않는다(콜론 때문에 \w+ 미매칭).
const ICON_TOKEN = /\{icon:(\w+)\}/g;

/**
 * raw 문자열을 세그먼트로 분해. `{icon:name}`은 icon, 실제 개행 문자(`\n`)는 newline,
 * 나머지는 text. 미지원 형식(`{icon:}`, `{foo}`)은 일반 텍스트로 그대로 통과한다.
 */
export function parseInlineTokens(raw: string): InlineSegment[] {
  const segments: InlineSegment[] = [];
  const pushText = (chunk: string): void => {
    if (!chunk) return;
    const lines = chunk.split("\n");
    lines.forEach((line, i) => {
      if (i > 0) segments.push({ kind: "newline" });
      if (line) segments.push({ kind: "text", text: line });
    });
  };

  let lastIndex = 0;
  ICON_TOKEN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = ICON_TOKEN.exec(raw)) !== null) {
    pushText(raw.slice(lastIndex, m.index));
    segments.push({ kind: "icon", name: m[1] ?? "" });
    lastIndex = m.index + m[0].length;
  }
  pushText(raw.slice(lastIndex));
  return segments;
}

/**
 * 세그먼트를 공백 경계 단어로 변환. 텍스트는 `\S+` 단위로 쪼개고 단어 뒤 공백 폭을 trailingSpace에 담는다.
 * 단어 앞 공백(아이콘 뒤 공백 등)은 **직전 단어의 trailingSpace에 합산**해 보존한다.
 *
 * @param measureText 텍스트 조각 폭 측정(px). @param measureIcon 아이콘 이름 → 폭(px). **미해결 아이콘은 null** 반환 → 단어에서 제외(뷰 렌더 skip과 일치, 빈공간 방지).
 */
export function buildInlineWords(
  segments: readonly InlineSegment[],
  measureText: (text: string) => number,
  measureIcon: (name: string) => number | null,
): InlineWord[] {
  const words: InlineWord[] = [];
  const addLeadingSpaceToPrev = (lead: string): void => {
    if (!lead) return;
    const prev = words[words.length - 1];
    if (prev && prev.kind !== "newline") prev.trailingSpaceWidth += measureText(lead);
  };

  for (const seg of segments) {
    if (seg.kind === "newline") {
      words.push({ kind: "newline" });
      continue;
    }
    if (seg.kind === "icon") {
      const iconWidth = measureIcon(seg.name);
      if (iconWidth === null) continue; // 미해결 아이콘 — 레이아웃에서 제외(뷰 렌더 skip과 일치, 빈공간 방지)
      words.push({ kind: "icon", name: seg.name, width: iconWidth, trailingSpaceWidth: 0 });
      continue;
    }
    // text — 앞공백·단어·뒷공백 캡처. 앞공백은 직전 단어 trailingSpace로(줄 시작이면 버림).
    const re = /(\s*)(\S+)(\s*)/g;
    let tm: RegExpExecArray | null;
    while ((tm = re.exec(seg.text)) !== null) {
      const lead = tm[1] ?? "";
      const token = tm[2] ?? "";
      const trail = tm[3] ?? "";
      addLeadingSpaceToPrev(lead);
      words.push({
        kind: "text",
        text: token,
        width: measureText(token),
        trailingSpaceWidth: trail ? measureText(trail) : 0,
      });
    }
  }
  return words;
}

/**
 * 단어를 wordWrap-aware로 배치. greedy line-break(`cursorX + width > maxWidth` → 새 줄),
 * 줄별 중앙정렬 오프셋, RTL 줄 내 미러를 적용한다.
 */
export function layoutInlineWords(
  words: readonly InlineWord[],
  opts: InlineLayoutOptions,
): InlineLayoutResult {
  const { maxWidth, lineHeight, align, dir } = opts;
  const placed: { word: Exclude<InlineWord, { kind: "newline" }>; localX: number; line: number }[] = [];
  let line = 0;
  let cursorX = 0;
  for (const w of words) {
    if (w.kind === "newline") {
      line += 1;
      cursorX = 0;
      continue;
    }
    if (cursorX > 0 && cursorX + w.width > maxWidth) {
      line += 1;
      cursorX = 0;
    }
    placed.push({ word: w, localX: cursorX, line });
    cursorX += w.width + w.trailingSpaceWidth;
  }

  const lineCount = (placed.length > 0 ? Math.max(...placed.map((p) => p.line)) : line) + 1;
  const lineWidths = new Array<number>(lineCount).fill(0);
  for (const p of placed) {
    const right = p.localX + p.word.width;
    if (right > (lineWidths[p.line] ?? 0)) lineWidths[p.line] = right;
  }

  const placements: InlinePlacement[] = placed.map((p) => {
    const lw = lineWidths[p.line] ?? 0;
    const offset = align === "center" ? (maxWidth - lw) / 2 : 0;
    const xInLine = mirrorX(p.localX, p.word.width, lw, dir);
    return { word: p.word, x: offset + xInLine, y: p.line * lineHeight, line: p.line };
  });

  return {
    placements,
    lineWidths,
    lineCount,
    totalHeight: lineCount * lineHeight,
    maxLineWidth: lineWidths.length > 0 ? Math.max(0, ...lineWidths) : 0,
  };
}
